/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import { InstallerToken, InstallerTokenFilters, PaginatedInstallerTokensResponse } from '@/client';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { RelativeTime } from '@/components/RelativeTime';
import { MimicTranslationFunction, useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

export type InstallerTokensTableProps = {
  installerTokensData: PaginatedInstallerTokensResponse;
  installerTokenFilters: InstallerTokenFilters;
  updateQueryParams: UpdateParams<InstallerToken, Partial<InstallerTokenFilters>>;
  revokeInstallerToken: (installerToken: InstallerToken) => void;
};

export function InstallerTokensTable({
  installerTokensData,
  updateQueryParams,
  installerTokenFilters,
  revokeInstallerToken,
}: InstallerTokensTableProps) {
  const { t } = useMimicTranslation('installerTokens');
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const columns = getInstallerTokenColumns(t, installerTokenFilters, revokeInstallerToken, tenantId);
  return (
    <PaginatedTable
      data={installerTokensData}
      columns={columns}
      rowKey={(token) => token.tokenId}
      data-testid="installer-tokens-table"
      onParamsChange={updateQueryParams}
    />
  );
}

function getInstallerTokenColumns(
  t: MimicTranslationFunction<'installerTokens'>,
  installerTokenFilters: InstallerTokenFilters,
  revokeInstallerToken: (installerToken: InstallerToken) => void,
  tenantId: string | undefined,
): ColumnsType<InstallerToken> {
  return [
    {
      title: <span data-testid="installerTokens.name">{t('table.tokenName')}</span>,
      dataIndex: 'name',
      sorter: true,
      width: '200px',
      filterMode: 'menu',
      render: (name) => {
        return (
          <Flex gap="xxs">
            <MimicHighlighter searchText={installerTokenFilters?.name} text={name} />
          </Flex>
        );
      },
    },
    {
      title: <span data-testid="installerTokens.jti">{t('table.jti')}</span>,
      dataIndex: 'tokenId',
      render: (tokenId: string) => (
        <Text mono copyable size="sm" data-testid="installer-token-jti">
          {tokenId}
        </Text>
      ),
      width: '340px',
    },
    {
      title: <span data-testid="installerTokens.createdBy">{t('table.createdBy')}</span>,
      dataIndex: 'createdBy',
      render: (_, token: InstallerToken) => {
        return (
          <Text size="md" type="subtle" data-testid="installer-token-created-by">
            {token.createdBy?.displayName}
          </Text>
        );
      },
    },
    {
      title: <span data-testid="installerTokens.timesUsed">{t('table.timesUsed')}</span>,
      dataIndex: 'quotaUsed',
      sorter: true,
      render: (quotaUsed: number) => (
        <Text size="md" type="subtle" data-testid="installer-token-times-used">
          {quotaUsed}
        </Text>
      ),
      width: '100px',
    },
    {
      title: <span data-testid="installerTokens.dateCreated">{t('table.dateCreated')}</span>,
      dataIndex: 'dateCreated',
      sorter: true,
      render: (dateCreated: string) => (
        <Text size="md" type="subtle" data-testid="installer-token-date-created">
          <RelativeTime date={dateCreated} />
        </Text>
      ),
      width: '180px',
    },
    {
      title: t('table.fleet'),
      dataIndex: 'nodeGroup',
      width: '175px',
      render: (_, token: InstallerToken) => {
        return token.nodeGroup ? (
          <Link to={`/tenants/${tenantId}/fleet-management/${token.nodeGroup.id}`}>
            <Text size="md" data-testid="installer-token-fleet">
              {token.nodeGroup.name}
            </Text>
          </Link>
        ) : (
          <Text size="md" type="subtle" data-testid="installer-token-fleet">
            -
          </Text>
        );
      },
    },
    {
      title: <span data-testid="installerTokens.revokeToken">{t('table.revoke')}</span>,
      dataIndex: 'tokenId',
      render: (_, installerToken: InstallerToken) => (
        <Button
          dataTestId="revoke-installer-token"
          type="primary"
          data-testid="revoke-button"
          onClick={() => revokeInstallerToken(installerToken)}
        >
          {t('table.revoke')}
        </Button>
      ),
      width: '120px',
    },
  ];
}
